/**
 * `?touchControls=1` / auto-detection flag for mobile touch controls.
 *
 * Supports:
 * - URL overrides: `?touchControls=1` (force on), `?touchControls=0` (force off)
 * - User setting: `localStorage.getItem('helix_touch_controls')` ('on' | 'off' | 'auto')
 * - Auto-detection: the PRIMARY pointer is touch — `(pointer: coarse)` (the media query reads the
 *   primary pointer; no hover clause, which some pen-equipped phones fail).
 *
 * Touch *capability* is not enough: desktop browsers report `maxTouchPoints > 0` and a coarse
 * secondary pointer for touchpads, touchscreen monitors and OS touch layers, while the player uses
 * a mouse. That showed the touch shell on desktop (DJ, v0.67.19). `maxTouchPoints` is only the
 * fallback when the browser cannot answer the media query.
 */
import { bootSearch } from '../../../config/boot/bootUrl';

export const TOUCH_CONTROLS_URL_FLAG = 'touchControls';
export const TOUCH_CONTROLS_STORAGE_KEY = 'helix_touch_controls';

export type TouchControlsPreference = 'auto' | 'on' | 'off';

/** True when the browser's primary input is touch (a phone or tablet), not merely touch-capable. */
export function isTouchDevice(): boolean {
  if (typeof window === 'undefined') return false;
  if (typeof window.matchMedia === 'function') return window.matchMedia('(pointer: coarse)').matches;
  const nav = typeof navigator !== 'undefined' ? navigator : null;
  return Boolean(nav && ((nav.maxTouchPoints ?? 0) > 0 || ((nav as any).msMaxTouchPoints ?? 0) > 0));
}

export function getTouchControlsPreference(): TouchControlsPreference {
  try {
    if (typeof localStorage !== 'undefined') {
      const val = localStorage.getItem(TOUCH_CONTROLS_STORAGE_KEY);
      if (val === 'on' || val === 'off' || val === 'auto') return val;
    }
  } catch {
    /* storage disabled */
  }
  return 'auto';
}

export function setTouchControlsPreference(pref: TouchControlsPreference): void {
  try {
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem(TOUCH_CONTROLS_STORAGE_KEY, pref);
    }
  } catch {
    /* storage disabled */
  }
}

/**
 * Returns true if touch controls should be active.
 * Explicit URL parameters take highest priority (`1` enables, `0` disables).
 * If no URL parameter is set, checks user setting and touch device capabilities.
 */
export function isTouchControlsEnabled(search?: string): boolean {
  const raw = typeof search === 'string'
    ? search
    : bootSearch();

  try {
    const params = new URLSearchParams(raw);
    const flag = params.get(TOUCH_CONTROLS_URL_FLAG);
    if (flag === '1') return true;
    if (flag === '0' || flag === 'false') return false;
  } catch {
    return false;
  }

  // If an explicit search string was supplied without the flag (e.g. from tests),
  // preserve the default-off contract.
  if (typeof search === 'string') {
    return false;
  }

  const pref = getTouchControlsPreference();
  if (pref === 'on') return true;
  if (pref === 'off') return false;

  return isTouchDevice();
}

